import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  LayoutGrid,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import api from '../api/client';
import {
  commitTaskFail,
  commitTaskToWeek,
  createTaskConstraint,
  deleteTaskConstraint,
  fetchLookahead,
  fetchLookaheadPpc,
  fetchTaskConstraints,
  uncommitTask,
  updateTaskConstraint,
} from '../api/lookahead';
import { fetchMaterialRequests } from '../api/materialRequests';
import type {
  CommitFailReason,
  LookaheadBoard,
  LookaheadPpc,
  LookaheadTask,
  MaterialRequest,
  MaterialRequestStatus,
  TaskConstraint,
  TaskConstraintType,
  TaskConstraintUserRef,
} from '../types';
import { useAuth } from '../hooks/useAuth';
import { useProject } from '../hooks/useProject';
import { hasPermission } from '../lib/permissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { getBackendError } from '../components/subcontractors/utils';

const STATUS_LABELS: Record<string, string> = {
  atandi: 'Atandı',
  yapiliyor: 'Yapılıyor',
  beklemede: 'Beklemede',
  tamamlandi: 'Tamamlandı',
  iptal_edildi: 'İptal Edildi',
  arsivlendi: 'Arşivlendi',
};

const STATUS_CLASSES: Record<string, string> = {
  atandi: 'bg-blue-100 text-blue-700 border-blue-200',
  yapiliyor: 'bg-amber-100 text-amber-700 border-amber-200',
  beklemede: 'bg-slate-100 text-slate-700 border-slate-200',
  tamamlandi: 'bg-green-100 text-green-700 border-green-200',
  iptal_edildi: 'bg-red-100 text-red-700 border-red-200',
  arsivlendi: 'bg-slate-100 text-slate-500 border-slate-200',
};

const PRIORITY_LABELS: Record<string, string> = {
  dusuk: 'Düşük',
  orta: 'Orta',
  yuksek: 'Yüksek',
  acil: 'Acil',
};

const PRIORITY_CLASSES: Record<string, string> = {
  dusuk: 'bg-slate-100 text-slate-600 border-slate-200',
  orta: 'bg-blue-100 text-blue-700 border-blue-200',
  yuksek: 'bg-orange-100 text-orange-700 border-orange-200',
  acil: 'bg-red-100 text-red-700 border-red-200',
};

const CONSTRAINT_TYPE_LABELS: Record<TaskConstraintType, string> = {
  malzeme: 'Malzeme',
  iscilik: 'İşçilik',
  onay: 'Onay',
  onceki_is: 'Önceki İş',
  ekipman: 'Ekipman',
  hava: 'Hava',
  diger: 'Diğer',
};

const CONSTRAINT_TYPES: TaskConstraintType[] = [
  'malzeme',
  'iscilik',
  'onay',
  'onceki_is',
  'ekipman',
  'hava',
  'diger',
];

const COMMIT_FAIL_REASON_LABELS: Record<CommitFailReason, string> = {
  malzeme: 'Malzeme',
  iscilik: 'İşçilik',
  hava: 'Hava',
  tasarim: 'Tasarım',
  onay: 'Onay',
  onceki_is: 'Önceki İş',
  diger: 'Diğer',
};

const COMMIT_FAIL_REASONS: CommitFailReason[] = [
  'malzeme',
  'iscilik',
  'hava',
  'tasarim',
  'onay',
  'onceki_is',
  'diger',
];

const MATERIAL_REQUEST_STATUS_LABELS: Record<MaterialRequestStatus, string> = {
  talep: 'Talep',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
  siparis_edildi: 'Sipariş Edildi',
  yolda: 'Kargoda',
  teslim_alindi: 'Teslim Alındı',
};

/** Haftanın 7 günü (Y-m-d dizisi, Pazartesi başlangıç). */
function weekDayKeys(weekStart: string): string[] {
  const start = parseDate(weekStart);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  });
}

const WEEKDAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

/** Kısıt yaşı (gün): backend age_days gönderirse o, yoksa created_at'ten hesap. */
function constraintAgeDays(constraint: TaskConstraint): number | null {
  if (typeof constraint.age_days === 'number') return constraint.age_days;
  if (!constraint.created_at) return null;
  const created = new Date(constraint.created_at.replace(' ', 'T'));
  if (Number.isNaN(created.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - created.getTime()) / 86_400_000));
}

/** Aging renk eşiği: 0-3 gri, 4-7 amber, 8+ kırmızı. */
function ageBadgeClass(ageDays: number): string {
  if (ageDays >= 8) return 'border-red-200 bg-red-100 text-red-700';
  if (ageDays >= 4) return 'border-amber-200 bg-amber-100 text-amber-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

/** Y-m-d → Date (yerel saat, parse kayması olmadan). */
function parseDate(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00`);
}

function formatDayMonth(date: Date): string {
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

/** Hafta başlığı: "20 Tem – 26 Tem" (Pazartesi + 6 gün). */
function formatWeekRange(weekStart: string): string {
  const start = parseDate(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return parseDate(iso).toLocaleDateString('tr-TR');
}

/** PPC renk eşiği: ≥80 yeşil, ≥50 amber, <50 kırmızı. */
function ppcColorClass(percent: number): string {
  if (percent >= 80) return 'text-emerald-600';
  if (percent >= 50) return 'text-amber-600';
  return 'text-red-600';
}

interface ConstraintFormState {
  type: TaskConstraintType;
  description: string;
  responsible_user_id: string;
  due_date: string;
  material_request_id: string;
}

const EMPTY_CONSTRAINT_FORM: ConstraintFormState = {
  type: 'malzeme',
  description: '',
  responsible_user_id: '',
  due_date: '',
  material_request_id: '',
};

/**
 * Lean/LPS lookahead panosu (başlıksız içerik): haftalık görev kovaları + PPC +
 * kısıt yönetimi. M5 (NAVIGASYON_YENIDEN_YAPILANMA_PLANI.md §2) ile Görevler
 * sayfasının 4. görünümü olarak render edilir; `/lookahead` `/tasks?view=lookahead`
 * adresine redirect olur. Kendi `<h1>`'i yoktur (plan M10 — çift başlık önlenir);
 * proje-seçili-değil empty state'i korunur.
 */
export default function LookaheadContent() {
  const { user } = useAuth();
  const { selectedProject } = useProject();

  const [weeksCount, setWeeksCount] = useState<3 | 6>(6);
  const [board, setBoard] = useState<LookaheadBoard | null>(null);
  const [ppc, setPpc] = useState<LookaheadPpc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);
  const [constraintsByTask, setConstraintsByTask] = useState<Record<number, TaskConstraint[]>>({});
  const [constraintsLoading, setConstraintsLoading] = useState(false);
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);

  const [constraintModalTask, setConstraintModalTask] = useState<LookaheadTask | null>(null);
  const [constraintForm, setConstraintForm] = useState<ConstraintFormState>(EMPTY_CONSTRAINT_FORM);
  const [constraintUsers, setConstraintUsers] = useState<TaskConstraintUserRef[]>([]);
  const [constraintSaving, setConstraintSaving] = useState(false);
  const [constraintError, setConstraintError] = useState<string | null>(null);
  const [constraintMaterialRequests, setConstraintMaterialRequests] = useState<MaterialRequest[]>([]);
  const [constraintDeleteTarget, setConstraintDeleteTarget] = useState<TaskConstraint | null>(null);
  const [constraintDeleting, setConstraintDeleting] = useState(false);

  // Ritüel güçlendirme: toplantı panosu + tutmadı akışı + kişi bazlı PPC
  const [boardMode, setBoardMode] = useState(false);
  const [boardCompact, setBoardCompact] = useState(false);
  const [ppcGroupBy, setPpcGroupBy] = useState<'general' | 'assignee'>('general');
  const [failModalTask, setFailModalTask] = useState<LookaheadTask | null>(null);
  const [failReason, setFailReason] = useState<CommitFailReason | ''>('');
  const [failNote, setFailNote] = useState('');
  const [failSaving, setFailSaving] = useState(false);
  const [failError, setFailError] = useState<string | null>(null);

  const canUpdate = hasPermission(user, 'tasks', 'update');

  const loadPpc = useCallback(
    async (weekStart: string) => {
      if (!selectedProject) return;
      try {
        const ppcData = await fetchLookaheadPpc({
          project_id: selectedProject.id,
          week: weekStart,
          group_by: ppcGroupBy === 'assignee' ? 'assignee' : undefined,
        });
        setPpc(ppcData);
      } catch {
        setPpc(null);
      }
    },
    [selectedProject, ppcGroupBy],
  );

  const loadBoard = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLookahead({ project_id: selectedProject.id, weeks: weeksCount });
      setBoard(data);
    } catch (err) {
      setError(getBackendError(err) || 'Lookahead panosu yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, weeksCount]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const currentWeekStart = board?.weeks[0]?.week_start ?? null;

  // PPC: hafta veya grup modu değişince yeniden çek (pano verisi korunur).
  useEffect(() => {
    if (currentWeekStart) {
      void loadPpc(currentWeekStart);
    } else {
      setPpc(null);
    }
  }, [currentWeekStart, loadPpc]);

  const loadConstraints = useCallback(async (taskId: number) => {
    setConstraintsLoading(true);
    try {
      const list = await fetchTaskConstraints(taskId);
      setConstraintsByTask((prev) => ({ ...prev, [taskId]: list }));
    } catch {
      // Liste yüklenemezse genişletilmiş bölümde hata göstermek yerine boş bırak.
    } finally {
      setConstraintsLoading(false);
    }
  }, []);

  const toggleExpand = async (taskId: number) => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
      return;
    }
    setExpandedTaskId(taskId);
    await loadConstraints(taskId);
  };

  const toggleCommit = async (task: LookaheadTask) => {
    if (!currentWeekStart) return;
    setBusyTaskId(task.id);
    try {
      if (task.committed) {
        await uncommitTask(task.id);
      } else {
        await commitTaskToWeek(task.id, currentWeekStart);
      }
      await loadBoard();
      // Taahhüt değişimi PPC sayaçlarını/dağılımını etkiler — kartı tazele
      // (loadPpc effect'i currentWeekStart değişmediğinde yeniden ateşlenmez).
      void loadPpc(currentWeekStart);
    } catch (err) {
      setError(getBackendError(err) || 'Taahhüt işlemi sırasında bir hata oluştu.');
    } finally {
      setBusyTaskId(null);
    }
  };

  const openFailModal = (task: LookaheadTask) => {
    setFailReason('');
    setFailNote('');
    setFailError(null);
    setFailModalTask(task);
  };

  const submitCommitFail = async () => {
    if (!failModalTask) return;
    if (!failReason) {
      setFailError('Neden seçimi zorunludur.');
      return;
    }
    setFailSaving(true);
    setFailError(null);
    try {
      await commitTaskFail(failModalTask.id, {
        reason: failReason,
        note: failNote.trim() || null,
      });
      setFailModalTask(null);
      await loadBoard();
      // Tutmadı kaydı PPC neden dağılımına yansır — kartı tazele.
      if (currentWeekStart) {
        void loadPpc(currentWeekStart);
      }
    } catch (err) {
      setFailError(getBackendError(err) || 'Tutmadı kaydı sırasında bir hata oluştu.');
    } finally {
      setFailSaving(false);
    }
  };

  const openConstraintModal = async (task: LookaheadTask) => {
    setConstraintForm(EMPTY_CONSTRAINT_FORM);
    setConstraintError(null);
    setConstraintModalTask(task);
    try {
      const res = await api.get('/api/v1/users/search', {
        params: { per_page: 100, exclude_self: false },
      });
      setConstraintUsers(res.data.data as TaskConstraintUserRef[]);
    } catch {
      setConstraintUsers([]);
    }
    // Malzeme kısıdı için bağlanabilecek açık talepler (terminal durumlar hariç).
    if (selectedProject) {
      try {
        const requests = await fetchMaterialRequests({ project_id: selectedProject.id });
        setConstraintMaterialRequests(
          requests.filter((r) => r.status !== 'reddedildi' && r.status !== 'teslim_alindi'),
        );
      } catch {
        setConstraintMaterialRequests([]);
      }
    } else {
      setConstraintMaterialRequests([]);
    }
  };

  const submitConstraint = async () => {
    if (!constraintModalTask) return;
    if (!constraintForm.description.trim()) {
      setConstraintError('Kısıt açıklaması zorunludur.');
      return;
    }
    setConstraintSaving(true);
    setConstraintError(null);
    try {
      const created = await createTaskConstraint(constraintModalTask.id, {
        type: constraintForm.type,
        description: constraintForm.description.trim(),
        responsible_user_id: constraintForm.responsible_user_id
          ? Number(constraintForm.responsible_user_id)
          : null,
        due_date: constraintForm.due_date || null,
        material_request_id: constraintForm.material_request_id
          ? Number(constraintForm.material_request_id)
          : null,
      });
      setConstraintsByTask((prev) => ({
        ...prev,
        [constraintModalTask.id]: [created, ...(prev[constraintModalTask.id] ?? [])],
      }));
      setConstraintModalTask(null);
      await loadBoard();
    } catch (err) {
      setConstraintError(getBackendError(err) || 'Kısıt kaydedilirken bir hata oluştu.');
    } finally {
      setConstraintSaving(false);
    }
  };

  const toggleResolve = async (constraint: TaskConstraint) => {
    try {
      const updated = await updateTaskConstraint(constraint.id, {
        status: constraint.status === 'acik' ? 'cozuldu' : 'acik',
      });
      setConstraintsByTask((prev) => ({
        ...prev,
        [constraint.task_id]: (prev[constraint.task_id] ?? []).map((c) =>
          c.id === updated.id ? updated : c,
        ),
      }));
      await loadBoard();
    } catch (err) {
      setError(getBackendError(err) || 'Kısıt güncellenirken bir hata oluştu.');
    }
  };

  const confirmRemoveConstraint = async () => {
    const constraint = constraintDeleteTarget;
    if (!constraint || constraintDeleting) return;
    setConstraintDeleting(true);
    try {
      await deleteTaskConstraint(constraint.id);
      setConstraintsByTask((prev) => ({
        ...prev,
        [constraint.task_id]: (prev[constraint.task_id] ?? []).filter(
          (c) => c.id !== constraint.id,
        ),
      }));
      setConstraintDeleteTarget(null);
      await loadBoard();
    } catch (err) {
      setConstraintDeleteTarget(null);
      setError(getBackendError(err) || 'Kısıt silinirken bir hata oluştu.');
    } finally {
      setConstraintDeleting(false);
    }
  };

  if (!selectedProject) {
    return (
      <div className="mx-auto max-w-7xl p-4 sm:p-6" data-testid="lookahead-page">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Lookahead panosunu görüntülemek için önce bir proje seçin.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6" data-testid="lookahead-page">
      <div className="mb-6 flex justify-end">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => setBoardMode((v) => !v)}
            data-testid="lookahead-board-toggle"
            aria-pressed={boardMode}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              boardMode
                ? 'bg-indigo-600 text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Pano
          </button>
          {boardMode && (
            <button
              onClick={() => setBoardCompact((v) => !v)}
              data-testid="lookahead-board-compact"
              aria-pressed={boardCompact}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                boardCompact
                  ? 'bg-slate-800 text-white'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              Büyük Metin
            </button>
          )}
          <div className="flex items-center gap-2" data-testid="lookahead-weeks-selector">
            <button
              onClick={() => setWeeksCount(3)}
              data-testid="lookahead-weeks-3"
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                weeksCount === 3
                  ? 'bg-blue-600 text-white'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              3 Hafta
            </button>
            <button
              onClick={() => setWeeksCount(6)}
              data-testid="lookahead-weeks-6"
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                weeksCount === 6
                  ? 'bg-blue-600 text-white'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              6 Hafta
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          data-testid="lookahead-error"
        >
          {error}
        </div>
      )}

      {/* PPC + özet kartları */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="lookahead-ppc-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              PPC — Bu Hafta (Plan Tamamlama %)
            </div>
            <div
              className="flex overflow-hidden rounded-lg border border-slate-200 text-[11px] font-medium"
              data-testid="ppc-group-by"
            >
              <button
                onClick={() => setPpcGroupBy('general')}
                data-testid="ppc-group-by-general"
                className={`px-2.5 py-1 ${
                  ppcGroupBy === 'general'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Genel
              </button>
              <button
                onClick={() => setPpcGroupBy('assignee')}
                data-testid="ppc-group-by-assignee"
                className={`px-2.5 py-1 ${
                  ppcGroupBy === 'assignee'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Kişi Bazlı
              </button>
            </div>
          </div>
          {ppc ? (
            <>
              <div className="mt-2 flex items-end justify-between gap-4">
                <div
                  className={`text-4xl font-bold ${ppcColorClass(ppc.ppc_percent)}`}
                  data-testid="lookahead-ppc-percent"
                >
                  %{ppc.ppc_percent.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                </div>
                <div className="text-right text-sm text-slate-600" data-testid="lookahead-ppc-counts">
                  <div>
                    Taahhüt: <span className="font-semibold text-slate-900">{ppc.committed_count}</span>
                  </div>
                  <div>
                    Tamamlanan:{' '}
                    <span className="font-semibold text-slate-900">{ppc.completed_count}</span>
                  </div>
                </div>
              </div>

              {ppcGroupBy === 'assignee' && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid="ppc-assignee-table">
                  {(!ppc.assignees || ppc.assignees.length === 0) ? (
                    <div className="text-xs text-slate-500">Kişi bazlı veri yok.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="pb-1 font-medium">Kullanıcı</th>
                          <th className="pb-1 text-right font-medium">Taahhüt</th>
                          <th className="pb-1 text-right font-medium">Tamamlanan</th>
                          <th className="pb-1 text-right font-medium">PPC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ppc.assignees.map((row) => (
                          <tr
                            key={row.user.id}
                            className="border-t border-slate-50"
                            data-testid={`ppc-assignee-${row.user.id}`}
                          >
                            <td className="py-1 text-slate-700">
                              {row.user.name} {row.user.surname}
                            </td>
                            <td className="py-1 text-right text-slate-600">{row.committed_count}</td>
                            <td className="py-1 text-right text-slate-600">{row.completed_count}</td>
                            <td className={`py-1 text-right font-semibold ${ppcColorClass(row.ppc_percent)}`}>
                              %{row.ppc_percent.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {ppc.reasons.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid="ppc-reasons">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Tutmadı Nedenleri
                  </div>
                  <div className="space-y-1">
                    {ppc.reasons.map((item) => {
                      const maxCount = Math.max(...ppc.reasons.map((r) => r.count), 1);
                      return (
                        <div
                          key={item.reason}
                          className="flex items-center gap-2 text-xs"
                          data-testid={`ppc-reason-${item.reason}`}
                        >
                          <span className="w-20 flex-none text-slate-600">
                            {COMMIT_FAIL_REASON_LABELS[item.reason as CommitFailReason] ?? item.reason}
                          </span>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-red-400"
                              style={{ width: `${Math.round((item.count / maxCount) * 100)}%` }}
                            />
                          </div>
                          <span className="w-6 flex-none text-right font-semibold text-slate-700">
                            {item.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-500">PPC hesaplanamadı.</div>
          )}
        </div>
        <div
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="lookahead-open-constraints"
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Açık Kısıtlar (Pencere Toplamı)
          </div>
          <div className="mt-2 flex items-center gap-2">
            <CircleAlert
              className={`h-7 w-7 ${
                (board?.open_constraints_total ?? 0) > 0 ? 'text-orange-500' : 'text-slate-300'
              }`}
            />
            <span className="text-3xl font-bold text-slate-900">
              {board?.open_constraints_total ?? 0}
            </span>
          </div>
        </div>
      </div>

      {loading && !board ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : boardMode && board?.weeks[0] ? (
        /* Toplantı panosu: bu haftanın gün sütunları (due_date'e göre salt-okunur
           gruplama); aksiyon "haftaya taahhüt" butonudur — güne taşıma yoktur,
           backend taahhüdü hafta başına (Pazartesi) normalize eder. */
        <div
          className={`grid grid-cols-2 gap-2 pb-4 sm:grid-cols-4 lg:grid-cols-7 ${
            boardCompact ? 'text-base' : 'text-sm'
          }`}
          data-testid="lookahead-board"
        >
          {(() => {
            const currentWeek = board.weeks[0];
            const dayKeys = weekDayKeys(currentWeek.week_start);
            const dayOfTask = (task: LookaheadTask): string =>
              task.due_date && dayKeys.includes(task.due_date.slice(0, 10))
                ? task.due_date.slice(0, 10)
                : currentWeek.week_start;
            return dayKeys.map((dayKey, dayIdx) => {
              const dayTasks = currentWeek.tasks.filter((t) => dayOfTask(t) === dayKey);
              const isToday = dayKey === new Date().toLocaleDateString('sv-SE');
              return (
                <div
                  key={dayKey}
                  data-testid={`lookahead-board-day-${dayKey}`}
                  className={`min-h-32 rounded-xl border p-2 ${
                    isToday ? 'border-blue-300 bg-blue-50/60' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div
                    className={`mb-2 flex items-baseline justify-between ${
                      boardCompact ? 'text-sm' : 'text-xs'
                    }`}
                  >
                    <span className="font-semibold text-slate-800">
                      {WEEKDAY_LABELS[dayIdx]}{' '}
                      <span className="font-normal text-slate-500">{formatDayMonth(parseDate(dayKey))}</span>
                    </span>
                    <span className="text-slate-500">{dayTasks.length}</span>
                  </div>
                  <div className="space-y-2">
                    {dayTasks.map((task) => (
                      <div
                        key={task.id}
                        data-testid={`lookahead-board-task-${task.id}`}
                        className={`rounded-lg border bg-white ${
                          boardCompact ? 'p-3' : 'p-2'
                        } ${
                          task.committed
                            ? 'border-emerald-300'
                            : 'border-slate-200'
                        }`}
                      >
                        <div
                          className={`font-medium text-slate-900 ${
                            boardCompact ? 'text-base' : 'text-xs'
                          }`}
                        >
                          {task.title}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span
                            className={`rounded-full border px-1.5 py-0.5 font-medium ${
                              boardCompact ? 'text-xs' : 'text-[10px]'
                            } ${STATUS_CLASSES[task.status] ?? STATUS_CLASSES.beklemede}`}
                          >
                            {STATUS_LABELS[task.status] ?? task.status}
                          </span>
                          {task.committed && (
                            <span
                              className={`inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700 ${
                                boardCompact ? 'text-xs' : 'text-[10px]'
                              }`}
                              data-testid={`task-committed-badge-${task.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Taahhüt
                            </span>
                          )}
                        </div>
                        {canUpdate && (
                          <div className="mt-1.5 flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => void toggleCommit(task)}
                              disabled={busyTaskId === task.id}
                              data-testid={`lookahead-board-commit-${task.id}`}
                              className={`w-full rounded-md border px-1 py-0.5 font-medium ${
                                boardCompact ? 'text-sm' : 'text-[10px]'
                              } disabled:opacity-50 ${
                                task.committed
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              {task.committed ? 'Taahhüdü Kaldır' : 'Haftaya Taahhüt Et'}
                            </button>
                            {task.committed &&
                              task.status !== 'tamamlandi' &&
                              task.status !== 'iptal_edildi' && (
                                <button
                                  onClick={() => openFailModal(task)}
                                  data-testid={`commit-fail-${task.id}`}
                                  title="Taahhüt tutmadı"
                                  className="flex-none rounded-md border border-red-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50"
                                >
                                  Tutmadı
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    ))}
                    {dayTasks.length === 0 && (
                      <div className="rounded-lg border border-dashed border-slate-200 p-2 text-center text-[10px] text-slate-500">
                        Görev yok
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="lookahead-board">
          {board?.weeks.map((week) => {
            const isCurrent = week.week_start === currentWeekStart;
            return (
              <div
                key={week.week_start}
                data-testid={`lookahead-week-${week.week_start}`}
                className={`w-72 flex-none rounded-xl border p-3 ${
                  isCurrent ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {formatWeekRange(week.week_start)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {week.tasks.length} görev
                      {isCurrent && <span className="ml-1 font-medium text-blue-600">· Bu hafta</span>}
                    </div>
                  </div>
                  {week.constraints_open_count > 0 && (
                    <span
                      className="rounded-full border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700"
                      data-testid={`lookahead-week-constraints-${week.week_start}`}
                    >
                      {week.constraints_open_count} açık kısıt
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  {week.tasks.length === 0 && (
                    <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-500">
                      Bu hafta görev yok
                    </div>
                  )}
                  {week.tasks.map((task) => {
                    const taskConstraints = constraintsByTask[task.id];
                    const openCount = taskConstraints
                      ? taskConstraints.filter((c) => c.status === 'acik').length
                      : null;
                    return (
                      <div
                        key={task.id}
                        data-testid={`lookahead-task-${task.id}`}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 text-sm font-medium text-slate-900">
                            {task.title}
                          </div>
                          <span
                            className={`flex-none rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                              PRIORITY_CLASSES[task.priority] ?? PRIORITY_CLASSES.orta
                            }`}
                          >
                            {PRIORITY_LABELS[task.priority] ?? task.priority}
                          </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span
                            className={`rounded-full border px-2 py-0.5 font-medium ${
                              STATUS_CLASSES[task.status] ?? STATUS_CLASSES.beklemede
                            }`}
                          >
                            {STATUS_LABELS[task.status] ?? task.status}
                          </span>
                          <span className="text-slate-500">Vade: {formatDate(task.due_date)}</span>
                        </div>
                        {task.assigned_users.length > 0 && (
                          <div className="mt-1 truncate text-[11px] text-slate-500">
                            {task.assigned_users.map((u) => `${u.name} ${u.surname}`).join(', ')}
                          </div>
                        )}

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {task.committed && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                              data-testid={`task-committed-badge-${task.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Taahhüt Edildi
                            </span>
                          )}
                          {openCount !== null && openCount > 0 && (
                            <span
                              className="rounded-full border border-orange-200 bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-700"
                              data-testid={`task-constraint-badge-${task.id}`}
                            >
                              {openCount} açık kısıt
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2">
                          {canUpdate && isCurrent && (
                            <button
                              onClick={() => void toggleCommit(task)}
                              disabled={busyTaskId === task.id}
                              data-testid={`task-commit-toggle-${task.id}`}
                              className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                                task.committed
                                  ? 'border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50'
                                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
                              } disabled:opacity-50`}
                            >
                              {task.committed ? 'Taahhüdü Kaldır' : 'Bu Haftaya Taahhüt'}
                            </button>
                          )}
                          {canUpdate &&
                            task.committed &&
                            task.status !== 'tamamlandi' &&
                            task.status !== 'iptal_edildi' &&
                            currentWeekStart !== null &&
                            week.week_start <= currentWeekStart && (
                              <button
                                onClick={() => openFailModal(task)}
                                data-testid={`commit-fail-${task.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="h-3 w-3" />
                                Tutmadı
                              </button>
                            )}
                          {canUpdate && (
                            <button
                              onClick={() => void openConstraintModal(task)}
                              data-testid={`constraint-add-${task.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <Plus className="h-3 w-3" />
                              Kısıt Ekle
                            </button>
                          )}
                          <button
                            onClick={() => void toggleExpand(task.id)}
                            data-testid={`task-constraints-toggle-${task.id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                          >
                            Kısıtlar
                            {expandedTaskId === task.id ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </button>
                        </div>

                        {expandedTaskId === task.id && (
                          <div
                            className="mt-2 space-y-1.5 rounded-lg bg-slate-50 p-2"
                            data-testid={`task-constraints-${task.id}`}
                          >
                            {constraintsLoading && (!taskConstraints || taskConstraints.length === 0) ? (
                              <div className="flex justify-center py-2">
                                <LoadingSpinner size="sm" />
                              </div>
                            ) : !taskConstraints || taskConstraints.length === 0 ? (
                              <div className="py-1 text-center text-[11px] text-slate-500">
                                Kısıt kaydı yok
                              </div>
                            ) : (
                              taskConstraints.map((constraint) => (
                                <div
                                  key={constraint.id}
                                  className="rounded-md border border-slate-200 bg-white p-2"
                                  data-testid={`constraint-item-${constraint.id}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-semibold text-slate-800">
                                      {CONSTRAINT_TYPE_LABELS[constraint.type] ?? constraint.type}
                                    </span>
                                    <span
                                      className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                                        constraint.status === 'acik'
                                          ? 'border-orange-200 bg-orange-100 text-orange-700'
                                          : 'border-emerald-200 bg-emerald-100 text-emerald-700'
                                      }`}
                                      data-testid={`constraint-status-${constraint.id}`}
                                    >
                                      {constraint.status === 'acik' ? 'Açık' : 'Çözüldü'}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[11px] text-slate-600">
                                    {constraint.description}
                                  </div>
                                  <div className="mt-0.5 text-[10px] text-slate-500">
                                    {constraint.responsible
                                      ? `Sorumlu: ${constraint.responsible.name} ${constraint.responsible.surname}`
                                      : 'Sorumlu yok'}
                                    {constraint.due_date
                                      ? ` · Termin: ${formatDate(constraint.due_date)}`
                                      : ''}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-1">
                                    {(() => {
                                      const age = constraintAgeDays(constraint);
                                      if (age === null) return null;
                                      return (
                                        <span
                                          className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${ageBadgeClass(age)}`}
                                          data-testid={`constraint-age-${constraint.id}`}
                                          title="Kısıt yaşı (gün)"
                                        >
                                          {age} gün
                                        </span>
                                      );
                                    })()}
                                    {constraint.type === 'malzeme' &&
                                      (constraint.material_request ? (
                                        <span
                                          className="rounded-full border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
                                          data-testid={`constraint-material-request-${constraint.id}`}
                                        >
                                          Talep #{constraint.material_request.id}:{' '}
                                          {MATERIAL_REQUEST_STATUS_LABELS[
                                            constraint.material_request.status
                                          ] ?? constraint.material_request.status}
                                        </span>
                                      ) : (
                                        <span
                                          className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                                          data-testid={`constraint-material-request-${constraint.id}`}
                                        >
                                          Talep yok
                                        </span>
                                      ))}
                                  </div>
                                  {canUpdate && (
                                    <div className="mt-1.5 flex items-center gap-1.5">
                                      <button
                                        onClick={() => void toggleResolve(constraint)}
                                        data-testid={`constraint-resolve-${constraint.id}`}
                                        className={`rounded-md px-2 py-0.5 text-[10px] font-medium ${
                                          constraint.status === 'acik'
                                            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                            : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {constraint.status === 'acik' ? 'Çöz' : 'Geri Aç'}
                                      </button>
                                      <button
                                        onClick={() => setConstraintDeleteTarget(constraint)}
                                        data-testid={`constraint-delete-${constraint.id}`}
                                        className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                        Sil
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Kısıt ekleme modalı */}
      {constraintModalTask && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="constraint-form-modal"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Kısıt Ekle</h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">{constraintModalTask.title}</p>

            {constraintError && (
              <div
                className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700"
                data-testid="constraint-form-error"
              >
                {constraintError}
              </div>
            )}

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Kısıt Tipi</label>
                <select
                  value={constraintForm.type}
                  onChange={(e) =>
                    setConstraintForm((f) => ({ ...f, type: e.target.value as TaskConstraintType }))
                  }
                  data-testid="constraint-form-type"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {CONSTRAINT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {CONSTRAINT_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Açıklama</label>
                <textarea
                  value={constraintForm.description}
                  onChange={(e) =>
                    setConstraintForm((f) => ({ ...f, description: e.target.value }))
                  }
                  maxLength={500}
                  rows={3}
                  data-testid="constraint-form-description"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Örn. Beton siparişi henüz verilmedi"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Sorumlu</label>
                  <select
                    value={constraintForm.responsible_user_id}
                    onChange={(e) =>
                      setConstraintForm((f) => ({ ...f, responsible_user_id: e.target.value }))
                    }
                    data-testid="constraint-form-responsible"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Seçilmedi</option>
                    {constraintUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} {u.surname}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Termin</label>
                  <input
                    type="date"
                    value={constraintForm.due_date}
                    onChange={(e) =>
                      setConstraintForm((f) => ({ ...f, due_date: e.target.value }))
                    }
                    data-testid="constraint-form-due-date"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              {constraintForm.type === 'malzeme' && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    Bağlı Malzeme Talebi (opsiyonel)
                  </label>
                  <select
                    value={constraintForm.material_request_id}
                    onChange={(e) =>
                      setConstraintForm((f) => ({ ...f, material_request_id: e.target.value }))
                    }
                    data-testid="constraint-form-material-request"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Talep bağlama</option>
                    {constraintMaterialRequests.map((r) => (
                      <option key={r.id} value={r.id}>
                        #{r.id} — {r.stock_item?.name ?? 'Malzeme'} ({r.quantity}) ·{' '}
                        {MATERIAL_REQUEST_STATUS_LABELS[r.status] ?? r.status}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConstraintModalTask(null)}
                data-testid="constraint-form-cancel"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Vazgeç
              </button>
              <button
                onClick={() => void submitConstraint()}
                disabled={constraintSaving}
                data-testid="constraint-form-submit"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {constraintSaving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Taahhüt tutmadı modalı */}
      <Modal
        open={failModalTask !== null}
        onClose={() => setFailModalTask(null)}
        title="Taahhüt Tutmadı"
        size="md"
        data-testid="commit-fail-modal"
      >
        <div className="p-6">
          <p className="truncate text-sm text-slate-500">{failModalTask?.title}</p>

          {failError && (
            <div
              className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700"
              data-testid="commit-fail-error"
            >
              {failError}
            </div>
          )}

          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Neden <span className="text-red-500">*</span>
              </label>
              <select
                value={failReason}
                onChange={(e) => setFailReason(e.target.value as CommitFailReason | '')}
                data-testid="commit-fail-reason"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Seçiniz…</option>
                {COMMIT_FAIL_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {COMMIT_FAIL_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Not (opsiyonel)
              </label>
              <textarea
                value={failNote}
                onChange={(e) => setFailNote(e.target.value)}
                maxLength={500}
                rows={3}
                data-testid="commit-fail-note"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Örn. Beton temin edilemedi, sevkiyat gecikti"
              />
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setFailModalTask(null)}
              data-testid="commit-fail-cancel"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Vazgeç
            </button>
            <button
              onClick={() => void submitCommitFail()}
              disabled={failSaving || !failReason}
              data-testid="commit-fail-submit"
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {failSaving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Kısıt silme onay dialogu */}
      <ConfirmDialog
        open={constraintDeleteTarget !== null}
        title="Kısıtı Sil"
        description="Bu kısıt kaydı silinsin mi?"
        confirmLabel="Sil"
        variant="danger"
        loading={constraintDeleting}
        onConfirm={() => void confirmRemoveConstraint()}
        onCancel={() => setConstraintDeleteTarget(null)}
      />
    </div>
  );
}
